import React, { useState, useEffect } from 'react';
import { AlertTriangle, MapPin, Calendar, Users, Globe, Tag, FolderHeart, Building2, Target, Shield, ChevronRight, FileText, Sparkles, ShoppingCart, ExternalLink } from 'lucide-react';
import { authAxios } from '../services/crmApi';
import { langFlag } from '../utils/tourLanguages';

function ClientPurchasesPanel({ applicationId }: { applicationId?: number }) {
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!applicationId) return;
    authAxios.get(`/api/crm/applications/${applicationId}/purchases`)
      .then(r => { if (Array.isArray(r.data)) setPurchases(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applicationId]);

  if (loading || purchases.length === 0) return null;

  const total = purchases.reduce((s, p) => s + (Number(p.total_price) || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden border-2 border-orange-300">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-3 flex items-center justify-between">
        <h3 className="font-bold text-white flex items-center text-lg">
          <ShoppingCart size={22} className="mr-2" />
          LISTA ZAKUPÓW KLIENTA
        </h3>
        <span className="bg-white/25 px-4 py-1.5 rounded-full text-white font-bold">
          {purchases.length} pozycji — {total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
        </span>
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 text-sm">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700 w-10">#</th>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Nazwa produktu</th>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700 w-24">Kategoria</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-700 w-32">Kwota brutto</th>
              <th className="text-center px-4 py-2.5 font-semibold text-gray-700 w-16">Link</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {purchases.map((p, i) => (
              <tr key={p.id || i} className={`border-t hover:bg-orange-50 transition-colors ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="px-4 py-2 text-gray-400 font-mono">{i + 1}</td>
                <td className="px-4 py-2 text-gray-900 font-medium">{p.item_name}</td>
                <td className="px-4 py-2"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{p.category || 'inne'}</span></td>
                <td className="px-4 py-2 text-right font-semibold text-gray-800">{Number(p.total_price || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
                <td className="px-4 py-2 text-center">{p.purchase_link ? <a href={p.purchase_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"><ExternalLink size={14} /></a> : <span className="text-gray-300">—</span>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-orange-50 border-t-2 border-orange-300">
            <tr>
              <td colSpan={3} className="px-4 py-3 font-bold text-gray-800 text-base">RAZEM</td>
              <td className="px-4 py-3 text-right font-bold text-orange-700 text-lg">{total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
import { OperatorAIChat } from './detail/OperatorAIChat';
import { InfoBox } from './Tooltip';
import { useApplicationDetail } from '../hooks/useApplicationDetail';
import { DetailHeader } from './detail/DetailHeader';
import { GrantPrecheckPanel } from './detail/GrantPrecheckPanel';
import { UniversalPrecheckPanel } from './detail/UniversalPrecheckPanel';
import { PersonalDataSection } from './detail/PersonalDataSection';
import { BusinessProfileSection } from './detail/BusinessProfileSection';
import { VerificationSection } from './detail/VerificationSection';
import { EducationEditSection, EducationViewSection } from './detail/EducationSection';
import { EmploymentEditSection, EmploymentViewSection } from './detail/EmploymentSection';
import { CoursesSection, CoursesViewSection } from './detail/CoursesSection';
import { CertificateArchive } from './detail/CertificateArchive';
import { AIAnalysisSection } from './detail/AIAnalysisSection';
import { DocumentsSection } from './detail/DocumentsSection';
import { DetailSidebar } from './detail/DetailSidebar';
import { DetailModals } from './detail/DetailModals';
import { useProductScoring } from '../hooks/useProductScoring';
import { LoanDrivePanel } from './detail/LoanDrivePanel';
import { ClientCrossReferencePanel } from './detail/ClientCrossReferencePanel';
import { OcrSuggestionsPanel } from './detail/OcrSuggestionsPanel';
import { GrantDetailSection } from './detail/GrantDetailSection';
import { DevGrantDetailSection } from './detail/DevGrantDetailSection';
import { ZUSDetailSection } from './detail/ZUSDetailSection';
import { EquipmentCatalogPanel } from './detail/EquipmentCatalogPanel';
import { BHPDetailSection } from './detail/BHPDetailSection';
import { KFSDetailSection } from './detail/KFSDetailSection';
import { isLoanProgram, getProgramByServiceType } from '../config/programs';
import { companiesApi } from '../services/companiesApi';
import { TripLinkedTripsPanel } from './detail/TripLinkedTripsPanel';
import { TripPhotosPanel } from './detail/TripPhotosPanel';
import { TripReviewsPanel } from './detail/TripReviewsPanel';
import { TripVoucherWidget } from './detail/TripVoucherWidget';
import { TripFinancePanel } from './detail/TripFinancePanel';
import { TurnusyMedicalPanel } from './detail/TurnusyMedicalPanel';
import UnifiedTimeline from './detail/UnifiedTimeline';
import { HutnikBiznesPanel } from './detail/HutnikBiznesPanel';
import { BookingSummaryWidget } from './detail/BookingSummaryWidget';
import { TurnusScheduleWidget } from './detail/TurnusScheduleWidget';
import { SponsorshipProgressWidget } from './detail/SponsorshipProgressWidget';
import { ProjectTimelineWidget } from './detail/ProjectTimelineWidget';
import { DocumentChecklistWidget } from './detail/DocumentChecklistWidget';
import { ProductRoadmap } from './detail/ProductRoadmap';
import { DeadlineTrackerWidget } from './detail/DeadlineTrackerWidget';
import { TrainingProgressWidget } from './detail/TrainingProgressWidget';

const PUP_PRODUCTS = new Set(['pup_grant', 'kfs', 'zus', 'bhp', 'zus_bhp_grant']);
const GRANT_PRODUCTS = new Set(['granty_fpn', 'dotacje_rozwojowe']);
const B2B_PRODUCTS = new Set(['hutnik_biznes', 'marketing_beauty', 'gastro_consulting']);
const TRIP_PRODUCTS = new Set(['wycieczki', 'turnusy']);

const GRANT_PROGRAM_SHORT: Record<string, string> = {
  proo_1a: 'PROO 1a', proo_1b: 'PROO 1b', proo_2: 'PROO 2', nowefio: 'NOWEFIO',
  ferc: 'FERC', fe_malopolska: 'FE Małopolska', erasmus: 'Erasmus+', cerv: 'CERV',
  kreator_kultury: 'Kreator Kultury', nck: 'NCK', mhp: 'MHP', bgk_pes: 'BGK PES', inne: 'Inny',
};

const PRODUCT_TIPS: Record<string, { title: string; items: string[] }> = {
  pup_grant: { title: 'Podpowiedzi — Dotacja PUP', items: [
    'Generuj analizę AI — Automatyczna ocena 23-punktowa wg regulaminu PUP Kraków 2026',
    'Raport Optymalizacji — AI przeanalizuje lokalizację, konkurencję, usprawnienia',
    'Lista Zakupów — Szczegółowa lista 15-25 produktów z linkami',
    'Zaświadczenia — Praktyki, wolontariat, referencje',
    'Kursy Fundacji NH — Rekomendowane kursy + generator certyfikatów',
    'Generuj wniosek PUP — Kompletny wniosek 2000-4000 słów',
  ]},
  wsparcie_na_starcie: { title: 'Podpowiedzi — Wsparcie na Starcie', items: [
    'Sprawdź kategorię — Zweryfikuj uprawnienia do pożyczki',
    'Przydziel operatora — PFP, FARR, FGSA, FRW, KSWP',
    'Biznesplan — Przygotuj lub pomóż w przygotowaniu',
    'Status pożyczki — Monitoruj w panelu bocznym (od zapytania do wypłaty)',
    'Umorzenie — Sprawdź kwalifikację (do 52 000 zł)',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  kfs: { title: 'Podpowiedzi — KFS Szkolenia', items: [
    'Uzasadnienie — Wygeneruj uzasadnienie szkolenia AI',
    'Dostawca szkolenia — Pomóż wybrać i zweryfikować',
    'Formularz PUP — Wypełnij wniosek KFS',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  granty_fpn: { title: 'Podpowiedzi — Granty FPN', items: [
    'Szkic wniosku — Opracuj koncepcję projektu',
    'Budżet — Przygotuj kosztorys i harmonogram',
    'Partnerzy — Zarządzaj konsorcjum (zagraniczny/krajowy)',
    'Baza wiedzy — Sprawdź regulaminy i wzory',
    'Deadline — Pilnuj terminu naboru!',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  dotacje_rozwojowe: { title: 'Podpowiedzi — Dotacje Rozwojowe', items: [
    'Kwalifikowalność — Zweryfikuj MŚP/NGO i region',
    'Program dotacyjny — Dopasuj do FEMP, FENG, FE Małopolska',
    'Wkład własny — Oblicz wymagany wkład (10-20%)',
    'Wniosek — Pomóż w przygotowaniu lub skieruj do eksperta',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  hutnik_biznes: { title: 'Podpowiedzi — Klub Biznesu', items: [
    'Profil firmy — Sprawdź branżę, cele, budżet',
    'Oferta sponsorska — Przygotuj pakiet Sponsor/Partner',
    'Follow-up — Zaplanuj kontakt i spotkanie',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  marketing_beauty: { title: 'Podpowiedzi — Marketing Beauty', items: [
    'Profil klienta — Sprawdź branżę i potrzeby marketingowe',
    'Pakiet usług — Dopasuj ofertę (branding, social media, strona)',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  gastro_consulting: { title: 'Podpowiedzi — Gastro Consulting', items: [
    'Profil lokalu — Sprawdź typ kuchni, lokalizację, wielkość',
    'Audyt — Przygotuj plan optymalizacji menu/kosztów',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
  wycieczki: { title: 'Podpowiedzi — eNHa TRIP', items: [
    'Trasa — Zweryfikuj trasę i dostępność przewodnika',
    'Utwórz wycieczkę — Przejdź do panelu wycieczek i przypisz przewodnika',
    'Wycena — Sprawdź kalkulację (cena/os × grupa)',
    'Potwierdźenie — Wyślij potwierdźenie rezerwacji do klienta',
    'Google Calendar — Wycieczka automatycznie pojawi się w kalendarzu zwiedzanie@',
    'Voucher — Sprawdź lub wygeneruj kod rabatowy',
  ]},
  turnusy: { title: 'Podpowiedzi — Turnusy Rehabilitacyjne', items: [
    'Dokumentacja — Zweryfikuj skierowanie i orzeczenie',
    'Termin — Potwierdź dostępność turnusu',
    'Dofinansowanie — Sprawdź PFRON/PCPR',
    'Upload dokumentów — Drag & drop na Google Drive',
  ]},
};

export const CRMApplicationDetail: React.FC = () => {
  const h = useApplicationDetail();
  const { scoringConfig } = useProductScoring(h.application?.service_type);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const st = h.application?.service_type || '';
  const isLoan = isLoanProgram(st);
  const isPup = PUP_PRODUCTS.has(st);
  const isGrant = GRANT_PRODUCTS.has(st);
  const isB2B = B2B_PRODUCTS.has(st);
  const isTrip = TRIP_PRODUCTS.has(st);
  const prog = getProgramByServiceType(st);
  const statusField = prog?.kanbanStatusField || 'status';

  if (h.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Ładowanie...</div>
      </div>
    );
  }

  if (!h.application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">Wniosek nie znaleziony</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DetailHeader h={h} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Guide — per product */}
            {(() => {
              const tips = PRODUCT_TIPS[h.application.service_type] || {
                title: 'Podpowiedzi dla Operatora',
                items: ['Upload dokumentów — Drag & drop na Google Drive', 'Status — Monitoruj w panelu bocznym', 'Notatki — Dodawaj notatki operatorskie'],
              };
              return (
                <InfoBox type="info" title={tips.title}>
                  <ul className="space-y-2">
                    {tips.items.map((item, i) => {
                      const [bold, rest] = item.split(' — ');
                      return <li key={i}><strong>{bold}</strong>{rest ? ` - ${rest}` : ''}</li>;
                    })}
                  </ul>
                </InfoBox>
              );
            })()}

            {/* Warnings */}
            {h.application.warnings?.hasWarnings && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <AlertTriangle className="text-yellow-600 mr-3" size={24} />
                  <h3 className="text-lg font-bold text-yellow-900">{h.application.warnings?.warningText}</h3>
                </div>
                <ul className="space-y-2">
                  {(h.application.warnings?.issues ?? []).map((issue: string, idx: number) => (
                    <li key={idx} className="text-sm text-yellow-800">• {issue}</li>
                  ))}
                </ul>
              </div>
            )}

            <PersonalDataSection h={h} />

            {/* Wycieczki Details Section — full trip view */}
            {isTrip && (
              <TripDetailsSection application={h.application} navigate={h.navigate} />
            )}

            {/* Booking Summary — compact trip card */}
            {st === 'wycieczki' && <BookingSummaryWidget applicationId={h.application.id} />}

            {/* ── Trip-specific panels (wycieczki/turnusy) ──────────────── */}
            {isTrip && (
              <>
                <TripLinkedTripsPanel applicationId={h.application.id} />
                <TripFinancePanel applicationId={h.application.id} />
                <TripVoucherWidget applicationId={h.application.id} email={h.application.email} />
                <TripPhotosPanel applicationId={h.application.id} />
                <TripReviewsPanel applicationId={h.application.id} email={h.application.email} />
              </>
            )}

            {/* Turnusy: Schedule + Medical profile */}
            {st === 'turnusy' && <TurnusScheduleWidget application={h.application} />}
            {st === 'turnusy' && (
              <TurnusyMedicalPanel application={h.application} onSaved={() => h.loadApplication()} />
            )}

            {/* Hutnik Biznes: Sponsorship Progress + Partnership panel */}
            {st === 'hutnik_biznes' && <SponsorshipProgressWidget application={h.application} />}
            {st === 'hutnik_biznes' && (
              <HutnikBiznesPanel application={h.application} onSaved={() => h.loadApplication()} />
            )}

            {/* Project Timeline — all products with roadmap */}
            <ProjectTimelineWidget application={h.application} />
            <ProductRoadmap productCode={st} currentStatus={(h.application as any)[statusField] || h.application.status} accentColor={prog?.color || 'blue'} />

            {/* Granty/Dotacje: Deadline Tracker */}
            {isGrant && <DeadlineTrackerWidget application={h.application} />}

            {/* KFS: Training Progress */}
            {st === 'kfs' && <TrainingProgressWidget application={h.application} />}

            {/* ── Non-trip sections ────────────────────────────────────── */}
            {!isTrip && (
              <OcrSuggestionsPanel
                applicationId={h.application.id}
                onFieldsApplied={() => h.loadApplication()}
              />
            )}
            {!isTrip && (isPup || isLoan || isB2B) && <BusinessProfileSection h={h} />}

            {/* ══════ LISTA ZAKUPÓW KLIENTA (centralny panel) ══════ */}
            <ClientPurchasesPanel applicationId={h.application?.id} />

            {st === 'granty_fpn' && (
              <GrantDetailSection application={h.application} onSaved={() => h.loadApplication()} />
            )}
            {st === 'dotacje_rozwojowe' && (
              <DevGrantDetailSection application={h.application} onSaved={() => h.loadApplication()} />
            )}
            {st === 'dotacje_rozwojowe' && h.application?.id && (
              <GrantPrecheckPanel applicationId={h.application.id} productCode={st} />
            )}
            {['pup_grant','szkolenie_kfs','kfs','turnusy','pozyczki_rozwojowe','pozyczka_rozwojowa','wsparcie_na_starcie','granty_fpn'].includes(st) && h.application?.id && (
              <UniversalPrecheckPanel applicationId={h.application.id} />
            )}
            {st === 'zus' && (
              <ZUSDetailSection application={h.application} onSaved={() => h.loadApplication()} />
            )}
            {st === 'bhp' && (
              <BHPDetailSection application={h.application} onSaved={() => h.loadApplication()} />
            )}
            {st === 'kfs' && (
              <KFSDetailSection application={h.application} onSaved={() => h.loadApplication()} />
            )}

            {!isTrip && isB2B && (h.application as any).company_name && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Building2 size={22} className="text-lime-600" />
                  Profil firmy
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InfoField label="Firma" value={(h.application as any).company_name} icon={<Building2 size={14} className="text-lime-500" />} />
                  <InfoField label="NIP" value={(h.application as any).nip} />
                  <InfoField label="Branża" value={(h.application as any).industry} />
                </div>
              </div>
            )}

            {!isTrip && (h.application as any).companySudop && (
              <CompanyDeMinimisPanel
                company={(h.application as any).companySudop}
                companyId={(h.application as any).company_id}
              />
            )}

            {isPup && h.editMode && <VerificationSection h={h} scoringConfig={scoringConfig} />}
            {isPup && h.editMode && <EducationEditSection h={h} />}
            {isPup && !h.editMode && <EducationViewSection h={h} />}
            {isPup && h.editMode && <EmploymentEditSection h={h} />}
            {isPup && !h.editMode && <EmploymentViewSection h={h} />}
            {isPup && h.editMode && <CoursesSection h={h} />}
            {isPup && !h.editMode && <CoursesViewSection h={h} />}
            {isPup && <CertificateArchive h={h} />}
            {isPup && <AIAnalysisSection h={h} />}

            {isLoan && (
              <LoanDrivePanel applicationId={h.application.id} gdriveId={h.application.gdrive_folder_id} />
            )}
            {isLoan && (
              <ClientCrossReferencePanel applicationId={h.application.id} email={h.application.email} pesel={h.application.pesel} />
            )}

            {!isTrip && (isPup || isLoan || isGrant || st === 'zus' || st === 'kfs') && (
              <EquipmentCatalogPanel programFilter={st} />
            )}

            <DocumentsSection h={h} />

            {/* Document Checklist — shows required vs uploaded docs */}
            {(isPup || isLoan || isGrant) && (
              <DocumentChecklistWidget applicationId={h.application.id} accentColor={isGrant ? 'violet' : isLoan ? 'amber' : 'blue'} />
            )}

            {/* Unified Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <FileText size={22} className="text-blue-600" />
                Oś czasu
              </h3>
              <UnifiedTimeline applicationId={h.application.id} />
            </div>
          </div>

          {/* Sidebar */}
          <DetailSidebar h={h} />
        </div>
      </div>

      <DetailModals h={h} />

      {/* Floating AI button */}
      <button
        onClick={() => setAiChatOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all z-40 flex items-center justify-center group"
        title="AI Asystent — analiza wniosku"
      >
        <Sparkles size={22} className="group-hover:animate-pulse" />
      </button>

      {/* AI Chat sidepanel */}
      {h.application && (
        <OperatorAIChat
          application={h.application}
          isOpen={aiChatOpen}
          onClose={() => setAiChatOpen(false)}
        />
      )}
    </div>
  );
};

// Company De Minimis Panel (SUDOP data from linked company)
const CompanyDeMinimisPanel: React.FC<{ company: any; companyId: number }> = ({ company }) => {
  const [showHistory, setShowHistory] = React.useState(false);
  const sd = company.sudop_data;
  if (!sd) return null;

  const fmtEur = (v: number) => (v || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pct = sd.utilizationPercent || 0;
  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-500' : 'bg-emerald-500';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Shield size={22} className="text-amber-600" />
        De minimis firmy — {company.name}
        <span className="text-xs font-normal text-gray-400 ml-auto">NIP: {company.nip}</span>
      </h3>

      {/* Progress bar */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-600">{fmtEur(sd.deMinimisSum3yEur)} EUR / 300 000 EUR</span>
        <span className="text-sm font-medium text-gray-700">{pct}%</span>
      </div>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-3">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500">Pozostało</p>
          <p className={`text-sm font-bold ${(sd.remainingEur || 0) > 50000 ? 'text-emerald-600' : 'text-orange-600'}`}>
            {fmtEur(sd.remainingEur)} EUR
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500">Przypadków</p>
          <p className="text-sm font-bold text-gray-700">{sd.totalCases || 0}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-xs text-amber-700">KFS (Urząd Pracy)</p>
          <p className="text-sm font-bold text-amber-800">{sd.kfsCases || 0}×</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-xs text-amber-700">Ostatnio KFS</p>
          <p className="text-sm font-bold text-amber-800">{sd.lastKfsDate || '—'}</p>
        </div>
      </div>

      {/* KFS amounts if available */}
      {sd.kfsTotalEur > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">Łączne wsparcie KFS</span>
            <span className="text-sm font-bold text-amber-900">{fmtEur(sd.kfsTotalEur)} EUR ({fmtEur(sd.kfsTotalPln)} PLN)</span>
          </div>
        </div>
      )}

      {/* KFS History */}
      {sd.kfsHistory && sd.kfsHistory.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 mb-2"
          >
            <ChevronRight size={14} className={`transition-transform ${showHistory ? 'rotate-90' : ''}`} />
            Historia KFS ({sd.kfsHistory.length} wpisów)
          </button>
          {showHistory && (
            <div className="space-y-1.5">
              {sd.kfsHistory.map((k: any, i: number) => (
                <div key={i} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2 text-sm">
                  <span className="text-gray-500">{k.date}</span>
                  <span className="text-gray-600 truncate mx-3 flex-1">{k.entity}</span>
                  <span className="font-mono font-medium text-gray-800">{fmtEur(k.eur)} EUR</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Warning */}
      {sd.exceedsLimit && (
        <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
          <span className="text-sm text-red-700">UWAGA: Przekroczono limit de minimis 300 000 EUR!</span>
        </div>
      )}

      {/* Download + footer */}
      <div className="mt-3 flex items-center justify-between">
        <button
          onClick={() => companiesApi.downloadSudopReport(company.id)}
          className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          <FileText size={14} />
          Pobierz raport Excel (KFS)
        </button>
        {company.sudop_checked_at && (
          <p className="text-xs text-gray-400">
            SUDOP UOKiK • {new Date(company.sudop_checked_at).toLocaleString('pl-PL')}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// TRIP DETAILS — dedicated section for wycieczki/turnusy
// ============================================================================

const ROUTE_NAMES: Record<string, string> = {
  kombinat: 'Budynek Z – Schron dowodzenia',
  socrealistyczna: 'Szlak Socrealistyczny',
  sakralna: 'Szlak Sakralny',
  prl: 'PRL w Nowej Hucie',
  nocna: 'Nocna Nowa Huta',
  objazdowa: 'Wycieczka Objazdowa',
  'mieszkanie-z-epoki': 'Mieszkanie z Epoki',
  'gra-miejska': 'Gra Miejska — Tajemnice Nowej Huty',
  'gra-cronos': 'Gra Terenowa CRONOS', // legacy slug, kept for old records
  'sesje-foto': 'Sesje Fotograficzne',
  'oferta-firmowa': 'Oferta Firmowa',
  'retro-przejazd-kombinat': 'Retro Przejazd – Kombinat',
  architektura: 'Szlak Architektury',
  'sladami-kina': 'Śladami Kina',
};

const TRIP_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  zapytanie: { label: 'Zapytanie', color: 'bg-gray-100 text-gray-700' },
  rezerwacja: { label: 'Rezerwacja', color: 'bg-yellow-100 text-yellow-800' },
  potwierdźona: { label: 'Potwierdzona', color: 'bg-green-100 text-green-800' },
  w_trakcie: { label: 'W trakcie', color: 'bg-blue-100 text-blue-800' },
  zakonczona: { label: 'Zakończona', color: 'bg-teal-100 text-teal-800' },
  anulowana: { label: 'Anulowana', color: 'bg-red-100 text-red-700' },
  voucher_inquiry: { label: 'Zapytanie o voucher', color: 'bg-purple-100 text-purple-700' },
};

const TripDetailsSection: React.FC<{ application: any; navigate: (path: string) => void }> = ({ application, navigate }) => {
  const a = application;
  const route = a.wycieczki_route;
  const routeName = ROUTE_NAMES[route] || route || '—';
  const date = a.wycieczki_preferred_date?.split('T')[0];
  const groupSize = a.wycieczki_group_size;
  const pricePerPerson = a.wycieczki_price_per_person ? Number(a.wycieczki_price_per_person) : null;
  const totalPrice = a.wycieczki_total_price ? Number(a.wycieczki_total_price) : null;
  const language = a.wycieczki_language?.toUpperCase() || 'PL';
  const isEnglishTour = language !== 'PL' && language !== 'POLISH';
  const groupType = a.wycieczki_group_type;
  const tripStatus = a.trip_status || 'zapytanie';
  const statusInfo = TRIP_STATUS_LABELS[tripStatus] || { label: tripStatus, color: 'bg-gray-100 text-gray-700' };

  return (
    <>
      {/* Trip Status Banner */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin size={24} className="text-white" />
            <div>
              <h3 className="text-lg font-bold text-white">{routeName}</h3>
              {date && <p className="text-teal-100 text-sm">{new Date(date + 'T00:00:00').toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEnglishTour && (
              <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-500 text-white animate-pulse">
                ENGLISH TOUR
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
        </div>

        <div className="p-6">
          {/* Key metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div className="bg-teal-50 rounded-xl p-4 text-center">
              <Users size={20} className="text-teal-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-teal-700">{groupSize || '?'}</p>
              <p className="text-xs text-teal-600">osób w grupie</p>
            </div>
            <div className={`rounded-xl p-4 text-center ${isEnglishTour ? 'bg-red-50 border-2 border-red-300' : 'bg-blue-50'}`}>
              <Globe size={20} className={`${isEnglishTour ? 'text-red-600' : 'text-blue-600'} mx-auto mb-1`} />
              <p className={`text-2xl font-bold ${isEnglishTour ? 'text-red-700' : 'text-blue-700'}`}>
                {langFlag(a.wycieczki_language)} {language}
              </p>
              <p className={`text-xs ${isEnglishTour ? 'text-red-600 font-semibold' : 'text-blue-600'}`}>
                {isEnglishTour ? 'wycieczka obcojęzyczna!' : 'język'}
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <Tag size={20} className="text-amber-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-amber-700">{pricePerPerson ? `${pricePerPerson} zł` : '—'}</p>
              <p className="text-xs text-amber-600">cena / osobę</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center border-2 border-emerald-200">
              <Target size={20} className="text-emerald-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-700">{totalPrice ? `${totalPrice.toLocaleString('pl-PL')} zł` : '—'}</p>
              <p className="text-xs text-emerald-600">razem</p>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <InfoField label="Trasa" value={routeName} icon={<MapPin size={14} className="text-teal-500" />} />
            <InfoField label="Data" value={date} icon={<Calendar size={14} className="text-blue-500" />} />
            <InfoField label="Grupa" value={groupSize ? `${groupSize} os.` : null} icon={<Users size={14} className="text-indigo-500" />} />
            <InfoField label="Język" value={`${langFlag(a.wycieczki_language)} ${language}`} icon={<Globe size={14} className="text-amber-500" />} />
            <InfoField label="Typ grupy" value={groupType} icon={<Tag size={14} className="text-purple-500" />} />
            <InfoField label="Źródło" value={a.source === 'wordpress_form' ? 'WordPress' : a.source === 'landing_wycieczki_rezerwacja' ? 'Landing' : a.source} icon={<FileText size={14} className="text-gray-500" />} />
          </div>

          {/* Attribution — which page the client wrote from. Stored all along,
              but never displayed, so operators could not tell the origin. */}
          {(a.landing_page || a.referrer || a.utm_source || a.utm_campaign) && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              {a.landing_page && (
                <div className="col-span-2 md:col-span-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Strona wejścia</div>
                  {/* Client-supplied value — never link it unless it is really http(s),
                      otherwise a javascript: URL would run in the operator's session. */}
                  {/^https?:\/\//i.test(a.landing_page) ? (
                    <a href={a.landing_page} target="_blank" rel="noopener noreferrer"
                       className="text-sm text-teal-700 hover:underline break-all">{a.landing_page}</a>
                  ) : (
                    <span className="text-sm text-gray-700 break-all">{a.landing_page}</span>
                  )}
                </div>
              )}
              <InfoField label="Skierowanie" value={a.referrer} icon={<Globe size={14} className="text-gray-500" />} />
              <InfoField label="UTM source" value={a.utm_source} icon={<Tag size={14} className="text-gray-500" />} />
              <InfoField label="Kampania" value={a.utm_campaign} icon={<Tag size={14} className="text-gray-500" />} />
            </div>
          )}

          {/* Special requests / notes */}
          {a.wycieczki_special_requests && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Szczegóły zgłoszenia</div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{a.wycieczki_special_requests}</p>
            </div>
          )}

          {/* Quick actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/wycieczki')}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              <MapPin size={16} />
              Panel wycieczek
            </button>
            {a.phone && (
              <a
                href={`tel:${a.phone}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <Users size={16} />
                Zadzwoń: {a.phone}
              </a>
            )}
            {a.email && (
              <a
                href={`mailto:${a.email}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Globe size={16} />
                Email
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Helper for wycieczki detail fields
const InfoField: React.FC<{ label: string; value?: string | null; icon?: React.ReactNode; highlight?: boolean }> = ({ label, value, icon, highlight }) => {
  if (!value) return null;
  return (
    <div className={`rounded-lg p-3 ${highlight ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-1 text-xs font-medium text-gray-500 mb-1">
        {icon}
        {label}
      </div>
      <div className={`text-sm font-semibold ${highlight ? 'text-teal-700' : 'text-gray-800'}`}>{value}</div>
    </div>
  );
};
